/* ============================================================
   FLUX 取扱店舗一覧 dealers-list.js（修正版 v2 / 2026-09-07）
   ------------------------------------------------------------
   データ取得の優先順:
     1. Storefront API（data-storefront-token が設定されている場合）
        メタオブジェクト（タイプ: store）を全件取得。
        クライアントが管理画面のメタオブジェクトを編集すると即反映される。
     2. セクション内の <script type="application/json" id="dealers-data">
        （空配列 [] 以外が入っていればそれを使う）
     3. data-data-url（assets/dealers-data.json）を fetch
     ※ 1が失敗した場合は自動で 2→3 にフォールバックする。
   受け付けるJSON形式:
     - { "stores": [ ...店舗... ] }  ← convert_dealers.py の出力
     - [ ...店舗... ]                ← 素の配列でも可
   店舗オブジェクト:
     { name, pref, region, zip, address, tel,
       types: ["bindings","boots"],
       models: { bindings: [...], boots: [...] },
       lat, lng }
     region / types は省略可（prefから自動判定）
   メタオブジェクト側のフィールドキー:
     customer_code, delivery_code, name, postal_code, prefecture,
     address, phone, latitude, longitude, bindings(リスト), boots(リスト)
   ============================================================ */
(function () {
  'use strict';

  var REGION_ORDER = ['北海道', '東北', '関東', '甲信越・北陸', '東海', '関西', '中国', '四国', '九州'];
  var REGION_PREFS = {
    '北海道': ['北海道'],
    '東北': ['青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県'],
    '関東': ['茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県'],
    '甲信越・北陸': ['新潟県', '山梨県', '長野県', '富山県', '石川県', '福井県'],
    '東海': ['静岡県', '愛知県', '岐阜県', '三重県'],
    '関西': ['滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県'],
    '中国': ['鳥取県', '島根県', '岡山県', '広島県', '山口県'],
    '四国': ['徳島県', '香川県', '愛媛県', '高知県'],
    '九州': ['福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県']
  };
  var PREF_TO_REGION = {};
  var PREF_ORDER = [];
  REGION_ORDER.forEach(function (r) {
    REGION_PREFS[r].forEach(function (p) {
      PREF_TO_REGION[p] = r;
      PREF_ORDER.push(p);
    });
  });
  var STOREFRONT_API_VERSION = '2026-01';
  var TYPES = ['bindings', 'boots', 'parts'];
  var TYPE_LABEL = { bindings: 'BIND', boots: 'BOOTS', parts: 'PARTS' };
  var TYPE_LABEL_JA = { bindings: 'バインディング', boots: 'ブーツ', parts: 'パーツ' };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function normalize(s) {
    return String(s == null ? '' : s)
      .normalize('NFKC')
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  function init(root) {
    var els = {
      q: root.querySelector('#dealers-q'),
      clear: root.querySelector('#dealers-clear'),
      pref: root.querySelector('#dealers-pref'),
      type: root.querySelector('#dealers-type'),
      model: root.querySelector('#dealers-model'),
      grid: root.querySelector('#dealers-grid'),
      count: root.querySelector('#dealers-count'),
      empty: root.querySelector('#dealers-empty'),
      error: root.querySelector('#dealers-error'),
      reset: root.querySelector('#dealers-reset'),
      viewBtns: root.querySelectorAll('.dealers__view-btn'),
      paneList: root.querySelector('#dealers-pane-list'),
      paneMap: root.querySelector('#dealers-pane-map'),
      map: root.querySelector('#dealers-map')
    };

    var stores = [];
    var mapObj = null;
    var mapLoaded = false;

    /* ---------- データ読み込み ---------- */
    loadData().then(function (list) {
      stores = normalizeStores(list);
      buildPrefOptions();
      buildModelOptions();
      applyInitialState();
      update();
    }).catch(function (err) {
      if (window.console) console.error('[dealers]', err);
      if (els.error) els.error.hidden = false;
    });

    function loadData() {
      var token = root.getAttribute('data-storefront-token');
      var domain = root.getAttribute('data-shop-domain');
      if (token && domain) {
        return loadFromStorefront(token, domain).catch(function (err) {
          if (window.console) console.warn('[dealers] Storefront API失敗。JSONにフォールバック:', err);
          return loadFromJson();
        });
      }
      return loadFromJson();
    }

    /* Storefront APIでメタオブジェクト（タイプ: store）を全件取得 */
    function loadFromStorefront(token, domain) {
      var endpoint = 'https://' + domain + '/api/' + STOREFRONT_API_VERSION + '/graphql.json';
      var type = root.getAttribute('data-metaobject-type') || 'store';
      var query =
        'query($type:String!,$cursor:String){' +
        'metaobjects(type:$type,first:250,after:$cursor){' +
        'pageInfo{hasNextPage endCursor}' +
        'nodes{handle fields{key value}}}}';
      var all = [];

      function fetchPage(cursor) {
        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({ query: query, variables: { type: type, cursor: cursor } })
        }).then(function (res) {
          if (!res.ok) throw new Error('Storefront API HTTP ' + res.status);
          return res.json();
        }).then(function (json) {
          if (json.errors) throw new Error(JSON.stringify(json.errors));
          var mo = json.data && json.data.metaobjects;
          if (!mo) throw new Error('metaobjects not in response');
          all = all.concat(mo.nodes || []);
          if (mo.pageInfo && mo.pageInfo.hasNextPage) return fetchPage(mo.pageInfo.endCursor);
          return all;
        });
      }

      return fetchPage(null).then(function (nodes) {
        var list = nodes.map(nodeToStore).filter(function (s) { return s.name; });
        if (!list.length) throw new Error('メタオブジェクトが0件（フォールバックします）');
        return list;
      });
    }

    /* メタオブジェクトの fields 配列 → 店舗オブジェクト */
    function nodeToStore(node) {
      var f = {};
      (node.fields || []).forEach(function (x) { f[x.key] = x.value; });
      return {
        name: f.name || '',
        pref: f.prefecture || '',
        zip: f.postal_code || '',
        address: f.address || '',
        tel: f.phone || '',
        year: f.model_year || '',
        sort: toNum(f.sort_order),
        lat: toNum(f.latitude),
        lng: toNum(f.longitude),
        models: {
          bindings: parseList(f.bindings),
          boots: parseList(f.boots),
          parts: parseList(f.parts)
        }
      };
    }

    function toNum(v) {
      if (v == null || v === '') return null;
      var n = Number(v);
      return isNaN(n) ? null : n;
    }

    /* リストフィールドはJSON文字列（'["CV","DS"]'）で返る。改行区切りも許容 */
    function parseList(v) {
      if (v == null || v === '') return [];
      if (Array.isArray(v)) return v;
      try {
        var p = JSON.parse(v);
        if (Array.isArray(p)) return p.map(String);
      } catch (e) { /* JSONでなければ区切り文字で分割 */ }
      return String(v).split(/[\n;,]+/).map(function (s) { return s.trim(); }).filter(Boolean);
    }

    function loadFromJson() {
      var inline = root.querySelector('#dealers-data');
      if (inline) {
        try {
          var parsed = JSON.parse(inline.textContent);
          var list = pickStores(parsed);
          if (list.length) return Promise.resolve(list);
        } catch (e) { /* インラインが壊れていたら fetch にフォールバック */ }
      }
      var url = root.getAttribute('data-data-url');
      if (!url) return Promise.reject(new Error('data-data-url not set'));
      return fetch(url, { credentials: 'omit' }).then(function (res) {
        if (!res.ok) throw new Error('fetch failed: ' + res.status);
        return res.json();
      }).then(pickStores);
    }

    function pickStores(data) {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.stores)) return data.stores;
      return [];
    }

    /* region・types・年式の補完と、地方→都道府県→店舗名 の並び替え */
    function normalizeStores(list) {
      var defaultYear = root.getAttribute('data-default-year') || '';
      list.forEach(function (s) {
        s.models = s.models || {};
        TYPES.forEach(function (t) { s.models[t] = s.models[t] || []; });
        if (!s.year) s.year = defaultYear; /* 店舗個別のmodel_yearが優先 */
        if (!s.region) s.region = PREF_TO_REGION[s.pref] || '';
        if (!s.types) {
          s.types = TYPES.filter(function (t) { return s.models[t].length; });
        }
      });
      list.sort(function (a, b) {
        var pa = PREF_ORDER.indexOf(a.pref); if (pa < 0) pa = 999;
        var pb = PREF_ORDER.indexOf(b.pref); if (pb < 0) pb = 999;
        if (pa !== pb) return pa - pb;
        /* 都道府県内: sort_order の昇順（未設定は後ろ）→ 郵便番号の昇順 → 店舗名順 */
        var sa = (a.sort == null) ? Infinity : Number(a.sort);
        var sb = (b.sort == null) ? Infinity : Number(b.sort);
        if (sa !== sb) return sa - sb;
        var za = String(a.zip || '9999999').replace(/[^0-9]/g, '');
        var zb = String(b.zip || '9999999').replace(/[^0-9]/g, '');
        if (za !== zb) return za < zb ? -1 : 1;
        return String(a.name).localeCompare(String(b.name), 'ja');
      });
      return list;
    }

    /* ---------- セレクト構築 ---------- */
    function buildPrefOptions() {
      var byRegion = {};
      stores.forEach(function (s) {
        if (!s.pref) return;
        var r = s.region || 'その他';
        (byRegion[r] = byRegion[r] || []).push(s.pref);
      });
      var regions = REGION_ORDER.filter(function (r) { return byRegion[r]; });
      Object.keys(byRegion).forEach(function (r) {
        if (regions.indexOf(r) === -1) regions.push(r);
      });
      regions.forEach(function (r) {
        var og = document.createElement('optgroup');
        og.label = r;
        uniqueInOrder(byRegion[r], prefIndex).forEach(function (p) {
          var op = document.createElement('option');
          op.value = p;
          op.textContent = p;
          og.appendChild(op);
        });
        els.pref.appendChild(og);
      });

      function prefIndex(p) {
        for (var i = 0; i < stores.length; i++) if (stores[i].pref === p) return i;
        return 9999;
      }
      function uniqueInOrder(arr, keyFn) {
        var seen = {};
        return arr.filter(function (v) {
          if (seen[v]) return false;
          seen[v] = true;
          return true;
        }).sort(function (a, b) { return keyFn(a) - keyFn(b); });
      }
    }

    function buildModelOptions() {
      var models = {};
      TYPES.forEach(function (t) { models[t] = []; });
      stores.forEach(function (s) {
        TYPES.forEach(function (t) {
          ((s.models || {})[t] || []).forEach(function (m) {
            if (models[t].indexOf(m) === -1) models[t].push(m);
          });
        });
      });
      TYPES.forEach(function (t) {
        if (!models[t].length) return;
        var og = document.createElement('optgroup');
        og.label = TYPE_LABEL_JA[t];
        models[t].sort().forEach(function (m) {
          var op = document.createElement('option');
          op.value = t + ':' + m;
          op.textContent = m;
          og.appendChild(op);
        });
        els.model.appendChild(og);
      });
    }

    /* ---------- 初期状態（メタフィールド／URLパラメータ） ---------- */
    function applyInitialState() {
      var params = new URLSearchParams(location.search);
      var q = params.get('q') || '';
      var pref = params.get('area') || '';
      var type = params.get('type') || root.getAttribute('data-initial-type') || '';
      var model = params.get('model') || root.getAttribute('data-initial-model') || '';

      if (q) els.q.value = q;
      if (pref && hasOption(els.pref, pref)) els.pref.value = pref;
      if (type && hasOption(els.type, type)) els.type.value = type;
      if (model) {
        var mv = resolveModelValue(model);
        if (mv) els.model.value = mv;
      }
    }

    function hasOption(sel, value) {
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].value === value) return true;
      }
      return false;
    }

    /* "DS" のようなモデルコード単体を "bindings:DS" 形式に解決する */
    function resolveModelValue(code) {
      if (hasOption(els.model, code)) return code;
      var n = normalize(code);
      for (var i = 0; i < els.model.options.length; i++) {
        var v = els.model.options[i].value;
        if (v && normalize(v.split(':')[1]) === n) return v;
      }
      return '';
    }

    /* ---------- フィルタリング ---------- */
    function currentFilter() {
      var mv = els.model.value;
      return {
        q: normalize(els.q.value),
        pref: els.pref.value,
        type: els.type.value,
        modelType: mv ? mv.split(':')[0] : '',
        model: mv ? mv.split(':')[1] : ''
      };
    }

    function match(s, f) {
      if (f.pref && s.pref !== f.pref) return false;
      if (f.type && (s.types || []).indexOf(f.type) === -1) return false;
      if (f.model) {
        var list = ((s.models || {})[f.modelType] || []);
        if (list.indexOf(f.model) === -1) return false;
      }
      if (f.q) {
        var hay = normalize([s.name, s.pref, s.address, s.zip].join(' '));
        if (hay.indexOf(f.q) === -1) return false;
      }
      return true;
    }

    /* ---------- 描画 ---------- */
    function card(s) {
      var mapsUrl = 'https://www.google.com/maps/search/?api=1&query=' +
        encodeURIComponent((s.name || '') + ' ' + (s.address || ''));
      var html = '<li class="dealers__card">';
      if (s.pref) html += '<span class="dealers__badge">' + esc(s.pref) + '</span>';
      html += '<h3 class="dealers__name">' + esc(s.name) + '</h3>';
      html += '<p class="dealers__addr">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" d="M12 21s7-5.1 7-11a7 7 0 1 0-14 0c0 5.9 7 11 7 11z"/><circle cx="12" cy="10" r="2.5" fill="none" stroke="currentColor" stroke-width="2"/></svg>' +
        (s.zip ? esc(s.zip) + ' ' : '') + esc(s.address) + '</p>';
      if (s.tel) {
        html += '<p class="dealers__tel">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" d="M4 5c0 8.3 6.7 15 15 15l2-4-4.5-2-2 2A11.6 11.6 0 0 1 8 9.5l2-2L8 3 4 5z"/></svg>' +
          '<a href="tel:' + esc(String(s.tel).replace(/[^0-9+\-]/g, '')) + '">' + esc(s.tel) + '</a></p>';
      }
      if (s.year) {
        html += '<p class="dealers__year">' + esc(s.year) + ' モデル</p>';
      }
      html += '<div class="dealers__tags">';
      TYPES.forEach(function (t) {
        var list = (s.models || {})[t] || [];
        if (!list.length) return;
        html += '<div class="dealers__tagrow">';
        html += '<span class="dealers__tag dealers__tag--' + t + '">' + TYPE_LABEL[t] + '</span>';
        list.forEach(function (m) {
          html += '<span class="dealers__chip">' + esc(m) + '</span>';
        });
        html += '</div>';
      });
      html += '</div>';
      html += '<a class="dealers__maplink" href="' + esc(mapsUrl) + '" target="_blank" rel="noopener">地図で見る' +
        '<svg viewBox="0 0 24 24" aria-hidden="true" width="12" height="12"><path fill="none" stroke="currentColor" stroke-width="2" d="M7 17 17 7M9 7h8v8"/></svg></a>';
      html += '</li>';
      return html;
    }

    function update() {
      var f = currentFilter();
      var hits = stores.filter(function (s) { return match(s, f); });

      els.grid.innerHTML = hits.map(card).join('');
      els.count.textContent = hits.length;
      els.empty.hidden = hits.length > 0;
      if (els.error) els.error.hidden = true;

      var active = !!(f.q || f.pref || f.type || f.model);
      els.reset.hidden = !active;
      els.clear.hidden = !els.q.value;

      syncUrl(f);
      if (mapObj) renderMarkers(hits);
    }

    function syncUrl(f) {
      var params = new URLSearchParams();
      if (els.q.value) params.set('q', els.q.value);
      if (f.pref) params.set('area', f.pref);
      if (f.type) params.set('type', f.type);
      if (f.model) params.set('model', els.model.value);
      var qs = params.toString();
      history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
    }

    /* ---------- 地図（Google Maps・任意） ---------- */
    var markers = [];
    function switchView(view) {
      els.viewBtns.forEach(function (b) {
        var on = b.getAttribute('data-view') === view;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (els.paneList) {
        els.paneList.classList.toggle('is-active', view === 'list');
        els.paneList.hidden = view !== 'list';
      }
      if (els.paneMap) {
        els.paneMap.classList.toggle('is-active', view === 'map');
        els.paneMap.hidden = view !== 'map';
      }
      if (view === 'map') ensureMap();
    }

    function ensureMap() {
      if (mapLoaded || !els.map) return;
      mapLoaded = true;
      var key = root.getAttribute('data-maps-key');
      if (!key) return;
      var cb = '__dealersMapInit' + Math.random().toString(36).slice(2);
      window[cb] = function () {
        mapObj = new google.maps.Map(els.map, {
          center: { lat: 38.5, lng: 137.0 },
          zoom: 5,
          mapTypeControl: false,
          streetViewControl: false
        });
        var f = currentFilter();
        renderMarkers(stores.filter(function (s) { return match(s, f); }));
        delete window[cb];
      };
      var sc = document.createElement('script');
      sc.src = 'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(key) + '&callback=' + cb;
      sc.async = true;
      document.head.appendChild(sc);
    }

    function renderMarkers(hits) {
      if (!mapObj || !window.google) return;
      markers.forEach(function (m) { m.setMap(null); });
      markers = [];
      var bounds = new google.maps.LatLngBounds();
      var info = new google.maps.InfoWindow();
      hits.forEach(function (s) {
        if (s.lat == null || s.lng == null) return;
        var pos = { lat: Number(s.lat), lng: Number(s.lng) };
        var mk = new google.maps.Marker({ map: mapObj, position: pos, title: s.name });
        mk.addListener('click', function () {
          info.setContent(
            '<strong>' + esc(s.name) + '</strong><br>' +
            esc((s.zip ? s.zip + ' ' : '') + (s.address || '')) +
            (s.tel ? '<br>TEL: ' + esc(s.tel) : '')
          );
          info.open(mapObj, mk);
        });
        markers.push(mk);
        bounds.extend(pos);
      });
      if (markers.length) mapObj.fitBounds(bounds);
    }

    /* ---------- イベント ---------- */
    var debounceTimer = null;
    els.q.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(update, 150);
    });
    els.clear.addEventListener('click', function () {
      els.q.value = '';
      update();
      els.q.focus();
    });
    [els.pref, els.type, els.model].forEach(function (sel) {
      sel.addEventListener('change', update);
    });
    els.reset.addEventListener('click', function () {
      els.q.value = '';
      els.pref.value = '';
      els.type.value = '';
      els.model.value = '';
      update();
    });
    els.viewBtns.forEach(function (b) {
      b.addEventListener('click', function () { switchView(b.getAttribute('data-view')); });
    });
  }

  function boot() {
    document.querySelectorAll('[data-dealers]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* テーマエディタ内でセクションが再描画されたときの対応 */
  document.addEventListener('shopify:section:load', function (ev) {
    var root = ev.target.querySelector('[data-dealers]');
    if (root) init(root);
  });
})();
